import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	IconButton,
	Divider,
	Paper,
	Chip
} from "@material-ui/core";
import {
	Close as CloseIcon,
	VerifiedUser as VerifiedUserIcon,
	Info as InfoIcon,
	PersonAdd as PersonAddIcon
} from "@material-ui/icons";
import { withStyles, Theme } from "@material-ui/core/styles";

interface ExistDependentAllowingModalProps {
	onClose: () => void;
	onConfirm: () => void;
	existingAltId?: string;
	dependentName?: string;
	isSSN?: boolean;
	existingMemberData?: {
		first_name?: string;
		last_name?: string;
		date_of_birth?: string;
		relationship?: string;
	};
}

const StyledDialog = withStyles((theme: Theme) => ({
	root: {
		"& .MuiDialog-paper": {
			borderRadius: 12,
			minWidth: 480,
			maxWidth: 520,
			boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
		}
	}
}))(Dialog);

const ExistDependentAllowingModal: React.FC<ExistDependentAllowingModalProps> = ({
	onClose,
	onConfirm,
	existingAltId = "",
	dependentName = "",
	isSSN = false,
	existingMemberData
}) => {
	const [cancelHovered, setCancelHovered] = useState(false);
	const [confirmHovered, setConfirmHovered] = useState(false);

	const displayValue = existingAltId || "N/A";
	const idType = isSSN ? "SSN" : "Alt ID";
	const idLabel = isSSN ? "Detected SSN" : "Detected Alt ID";

	return (
		<StyledDialog
			open={true}
			onClose={onClose}
			aria-labelledby="existing-dependent-dialog-title"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle
				id="existing-dependent-dialog-title"
				style={{
					padding: "20px 24px 0 24px",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center"
				}}
			>
				<Typography
					variant="h6"
					style={{
						fontWeight: 600,
						color: "#1F2937",
						fontSize: "1.25rem"
					}}
				>
					Existing Family Member Detected
				</Typography>
				<IconButton
					onClick={onClose}
					style={{
						color: "#9CA3AF",
						padding: 8,
						marginRight: -12
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent style={{ padding: "24px" }}>
				<Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
					{/* Status Chip */}
					<Chip
						icon={<InfoIcon style={{ fontSize: 16 }} />}
						label="Match Found"
						style={{
							backgroundColor: "#DBEAFE",
							color: "#1E40AF",
							fontWeight: 600,
							marginBottom: 16,
							border: "1px solid #BFDBFE",
							padding: "4px 8px"
						}}
					/>

					{/* Main Message Paper */}
					<Paper
						elevation={0}
						style={{
							backgroundColor: "#EFF6FF",
							border: "1px solid #BFDBFE",
							borderRadius: 8,
							padding: 16,
							width: "100%",
							marginBottom: 16,
							display: "flex",
							alignItems: "flex-start",
							gap: 12
						}}
					>
						<InfoIcon style={{ color: "#3B82F6", fontSize: 24, marginTop: 2 }} />
						<Box textAlign="left">
							<Typography
								variant="subtitle2"
								style={{
									color: "#1E40AF",
									fontWeight: 600,
									marginBottom: 4
								}}
							>
								System Match
							</Typography>
							<Typography variant="body2" style={{ color: "#1E40AF", lineHeight: 1.5 }}>
								We found an existing family member in our system with matching <strong>{idType}</strong>{" "}
								information.
							</Typography>
						</Box>
					</Paper>

					{/* Detailed Message */}
					<Paper
						elevation={0}
						style={{
							backgroundColor: "#F0FDF4",
							borderRadius: 8,
							padding: 20,
							width: "100%",
							borderLeft: "4px solid #22C55E",
							marginBottom: 16,
							textAlign: "left"
						}}
					>
						<Typography
							variant="body1"
							style={{
								color: "#166534",
								lineHeight: 1.6,
								fontWeight: 500,
								marginBottom: 12
							}}
						>
							A family member named{" "}
							<strong>
								{existingMemberData?.first_name} {existingMemberData?.last_name}
							</strong>{" "}
							already exists in our system.
						</Typography>

						<Divider style={{ margin: "12px 0", backgroundColor: "#86EFAC" }} />

						<Typography
							variant="body2"
							style={{
								color: "#166534",
								lineHeight: 1.5
							}}
						>
							Would you like to add this existing family member to <strong>{dependentName}</strong>s
							profile?
						</Typography>
					</Paper>

					{/* Info Details - Shows SSN or ALT ID */}
					<Paper
						elevation={0}
						style={{
							backgroundColor: "#FAFAFA",
							borderRadius: 6,
							padding: "12px 16px",
							width: "100%",
							border: "1px solid #E5E7EB",
							display: "flex",
							flexDirection: "column",
							gap: 8
						}}
					>
						<Box display="flex" alignItems="center">
							<VerifiedUserIcon style={{ color: "#6B7280", marginRight: 12, fontSize: 20 }} />
							<Box textAlign="left" flex={1}>
								<Typography variant="caption" style={{ color: "#6B7280", display: "block" }}>
									{idLabel}
								</Typography>
								<Typography
									variant="body2"
									style={{
										color: "#374151",
										fontWeight: 600,
										fontFamily: "monospace",
										fontSize: "0.95rem"
									}}
								>
									{displayValue}
								</Typography>
							</Box>
						</Box>

						{existingMemberData?.date_of_birth && (
							<Box display="flex" alignItems="center">
								<InfoIcon style={{ color: "#6B7280", marginRight: 12, fontSize: 20 }} />
								<Box textAlign="left" flex={1}>
									<Typography variant="caption" style={{ color: "#6B7280", display: "block" }}>
										Date of Birth
									</Typography>
									<Typography
										variant="body2"
										style={{
											color: "#374151",
											fontWeight: 500,
											fontSize: "0.95rem"
										}}
									>
										{existingMemberData.date_of_birth}
									</Typography>
								</Box>
							</Box>
						)}

						{existingMemberData?.relationship && (
							<Box display="flex" alignItems="center">
								<PersonAddIcon style={{ color: "#6B7280", marginRight: 12, fontSize: 20 }} />
								<Box textAlign="left" flex={1}>
									<Typography variant="caption" style={{ color: "#6B7280", display: "block" }}>
										Current Relationship
									</Typography>
									<Typography
										variant="body2"
										style={{
											color: "#374151",
											fontWeight: 500,
											fontSize: "0.95rem",
											textTransform: "capitalize"
										}}
									>
										{existingMemberData.relationship}
									</Typography>
								</Box>
							</Box>
						)}
					</Paper>
				</Box>
			</DialogContent>

			<DialogActions
				style={{
					padding: "16px 24px 24px 24px",
					justifyContent: "center",
					gap: 12
				}}
			>
				{/* Cancel Button */}
				<Button
					onClick={onClose}
					variant="outlined"
					size="large"
					onMouseEnter={() => setCancelHovered(true)}
					onMouseLeave={() => setCancelHovered(false)}
					style={{
						color: cancelHovered ? "#374151" : "#6B7280",
						borderColor: cancelHovered ? "#9CA3AF" : "#D1D5DB",
						borderWidth: 2,
						textTransform: "none",
						fontWeight: 600,
						padding: "10px 24px",
						borderRadius: 8,
						transition: "all 0.2s ease",
						minWidth: 120
					}}
				>
					Cancel
				</Button>

				{/* Confirm Button */}
				<Button
					onClick={onConfirm}
					variant="contained"
					size="large"
					onMouseEnter={() => setConfirmHovered(true)}
					onMouseLeave={() => setConfirmHovered(false)}
					style={{
						backgroundColor: confirmHovered ? "#16A34A" : "#22C55E",
						color: "white",
						textTransform: "none",
						fontWeight: 600,
						padding: "10px 32px",
						borderRadius: 8,
						boxShadow: confirmHovered
							? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
							: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
						transition: "all 0.2s ease",
						minWidth: 140
					}}
				>
					Yes, Add Them
				</Button>
			</DialogActions>
		</StyledDialog>
	);
};

export default ExistDependentAllowingModal;
